import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { MetadataList } from '../../shared/detail/MetadataList';
import { PageState } from '../../shared/data/PageState';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { apiReportKindLabel, reportFilterLabel } from '../reports/labels';
import { reportExportsRepositoryToken } from './repository';

export function ReportExportDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(reportExportsRepositoryToken);
  const auth = useAuth();
  const { exportId = '' } = useParams();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refresh, setRefresh] = useState(0);
  const [downloadError, setDownloadError] = useState<AppError | null>(null);
  const downloading = useRef(false);
  const resource = useResource(
    (signal) => repository.get(exportId, signal),
    [exportId, refresh, repository],
  );
  const detail = resource.data;
  const expired =
    detail === null || Date.parse(detail.expiresAt) <= Date.now() || detail.status === 'EXPIRED';
  const canUseExports =
    auth.status === 'authenticated' &&
    auth.principal.permissions.includes('REPORT_VIEW') &&
    auth.principal.permissions.includes('REPORT_EXPORT');
  const canDownload = detail?.status === 'COMPLETED' && !expired && canUseExports;
  const download = async () => {
    if (!canDownload || downloading.current) return;
    downloading.current = true;
    setDownloadError(null);
    let objectUrl: string | null = null;
    try {
      const result = await repository.download(exportId, new AbortController().signal);
      objectUrl = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = result.filename ?? `report-${detail.id}.csv`;
      anchor.click();
    } catch (error) {
      setDownloadError(error as AppError);
    } finally {
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
      downloading.current = false;
    }
  };
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <Stack direction="row" spacing={1}>
            <DataSourceBadge source={dataSource} />
            <Button component={Link} to={paths.reportExports}>
              {t('nav.reportExports')}
            </Button>
          </Stack>
        }
        subtitle={t('reportExports.subtitle')}
        title={t('reportExports.detail')}
      />
      <PageState
        error={safeError(resource.error, t('errors.generic'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {detail === null ? null : (
          <Stack spacing={2}>
            <Paper
              aria-label={t('reportExports.metadataTitle')}
              component="section"
              sx={{ p: 2 }}
              variant="outlined"
            >
              <Stack spacing={1}>
                <Chip label={detail.status} sx={{ alignSelf: 'flex-start' }} />
                <Typography component="h2" variant="h6">
                  {apiReportKindLabel(detail.reportType, t)}
                </Typography>
                <MetadataList
                  items={[
                    { label: t('reportExports.format'), value: detail.format },
                    { label: t('reportExports.requestedBy'), value: detail.requestedBy },
                    {
                      label: t('reportExports.partnerScope'),
                      value: detail.partnerId ?? t('common.notAvailable'),
                    },
                    {
                      label: t('reportExports.agentScope'),
                      value: detail.agentId ?? t('common.notAvailable'),
                    },
                    { label: t('reportExports.timezone'), value: detail.timezone },
                    {
                      label: t('reportExports.currency'),
                      value: detail.currencyCode ?? t('common.notAvailable'),
                    },
                    {
                      label: t('reportExports.createdAt'),
                      value: timestamp(
                        detail.createdAt,
                        detail.timezone,
                        language,
                        t('common.notAvailable'),
                      ),
                    },
                    {
                      label: t('reportExports.generatedAt'),
                      value: timestamp(
                        detail.generatedAt,
                        detail.timezone,
                        language,
                        t('common.notAvailable'),
                      ),
                    },
                    {
                      label: t('reportExports.completedAt'),
                      value: timestamp(
                        detail.completedAt,
                        detail.timezone,
                        language,
                        t('common.notAvailable'),
                      ),
                    },
                    {
                      label: t('reportExports.updatedAt'),
                      value: timestamp(
                        detail.updatedAt,
                        detail.timezone,
                        language,
                        t('common.notAvailable'),
                      ),
                    },
                    {
                      label: t('reportExports.expiresAt'),
                      value: timestamp(
                        detail.expiresAt,
                        detail.timezone,
                        language,
                        t('common.notAvailable'),
                      ),
                    },
                    {
                      label: t('reportExports.snapshotId'),
                      value: detail.snapshotId ?? t('common.notAvailable'),
                    },
                    {
                      label: t('reportExports.recordCount'),
                      value: detail.recordCount ?? t('common.notAvailable'),
                    },
                    {
                      label: t('reportExports.contentLength'),
                      value: detail.contentLength ?? t('common.notAvailable'),
                    },
                  ]}
                />
                {detail.requestId === null ? null : (
                  <Typography variant="caption">
                    {t('reports.requestId', { id: detail.requestId })}
                  </Typography>
                )}
                <Typography color="text.secondary" variant="body2">
                  {t('reportExports.filenameNotice')}
                </Typography>
              </Stack>
            </Paper>
            <Paper
              aria-label={t('reportExports.filtersTitle')}
              component="section"
              sx={{ p: 2 }}
              variant="outlined"
            >
              <Typography component="h2" variant="h6">
                {t('reportExports.filtersTitle')}
              </Typography>
              <MetadataList
                items={Object.entries(detail.filters).map(([key, value]) => ({
                  label: reportFilterLabel(key, t),
                  value: String(value),
                }))}
              />
            </Paper>
            {expired ? (
              <Alert severity="warning">{t('reportExports.expired')}</Alert>
            ) : detail.status === 'FAILED' ? (
              <Alert severity="error">
                {t('reportExports.failed')} {t('reportExports.failureCode')}:{' '}
                {safeFailureCode(detail.failureCode, t('common.unknown'))}
              </Alert>
            ) : canDownload ? (
              <Button
                onClick={() => void download()}
                sx={{ alignSelf: 'flex-start' }}
                variant="contained"
              >
                {t('reportExports.download')}
              </Button>
            ) : (
              <Alert severity="info">{t('reportExports.notReady')}</Alert>
            )}
            {downloadError === null ? null : (
              <Alert severity="error">
                {t('reportExports.downloadError')}
                {downloadError.requestId === null
                  ? ''
                  : ` · ${t('reports.requestId', { id: downloadError.requestId })}`}
              </Alert>
            )}
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function timestamp(
  value: string | null,
  timezone: string,
  language: TimeLanguage,
  fallback: string,
): string {
  if (value === null) return fallback;
  const formatted = formatTimestamp(value, timezone, language);
  return `${formatted.display} (${formatted.originalOffset})`;
}

function safeFailureCode(value: string | null, fallback: string): string {
  return value !== null && /^[A-Z][A-Z0-9_]{0,99}$/u.test(value) ? value : fallback;
}
function safeError(error: unknown, fallback: string): AppError | null {
  if (error === null) return null;
  const requestId =
    typeof error === 'object' &&
    error !== null &&
    'requestId' in error &&
    typeof error.requestId === 'string'
      ? error.requestId
      : null;
  return {
    name: 'AppError',
    message: fallback,
    status: 0,
    code: 'REPORT_EXPORT_ERROR',
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  } as AppError;
}
